import { useMemo, useRef } from "react";
import { useInView } from "../../RoyalMahal/hooks";
import { between, createRng, round } from "./shapes";

/*
 * Holi colours: clouds of gulal bursting behind the closing message, each throwing out
 * a spray of powder. When the page comes into view they all burst together, a little
 * staggered; after that each repeats on its own rhythm, so the sky keeps blooming.
 * Clouds are layered radial gradients (no blur filters), so they stay light to animate.
 */

// Rani pink, marigold, haldi, teal, purple, parrot green, rose
const COLORS = ["216, 27, 96", "243, 154, 18", "246, 196, 69", "17, 147, 143", "123, 63, 160", "95, 168, 58", "244, 143, 177"];

// Where the clouds burst (percent of the page), around the message rather than over it
const SPOTS = [
  [14, 14],
  [86, 12],
  [50, 6],
  [8, 44],
  [92, 42],
  [28, 30],
  [72, 28],
  [50, 52],
  [20, 62],
  [80, 60],
];

const DOTS = 9;

const ColorBlast = ({ seed = 14, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.15 });

  const bursts = useMemo(() => {
    const rng = createRng(seed);
    return SPOTS.map(([left, top], i) => ({
      left,
      top,
      color: COLORS[i % COLORS.length],
      size: round(between(rng, 150, 260)),
      delay: round(i * 0.16 + between(rng, 0, 0.2)),
      duration: round(between(rng, 4.6, 9)),
      // Lobes that make each cloud irregular, like thrown powder
      lobes: Array.from({ length: 3 }, () => [round(between(rng, 32, 68)), round(between(rng, 32, 68))]),
      dots: Array.from({ length: DOTS }, (_, k) => {
        const angle = (k / DOTS) * Math.PI * 2 + between(rng, -0.3, 0.3);
        const distance = between(rng, 70, 140);
        return {
          dx: round(Math.cos(angle) * distance),
          dy: round(Math.sin(angle) * distance),
          size: round(between(rng, 4, 9)),
        };
      }),
    }));
  }, [seed]);

  return (
    <div ref={ref} className={`ut-blast ${inView ? "is-in" : ""} ${className}`} aria-hidden="true">
      {bursts.map((b, i) => (
        <div
          key={i}
          className="ut-blast__burst"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            "--c": b.color,
            "--size": `${b.size}px`,
            "--delay": `${b.delay}s`,
            "--duration": `${b.duration}s`,
          }}
        >
          <span
            className="ut-blast__cloud"
            style={{
              backgroundImage: [
                ...b.lobes.map(([x, y]) => `radial-gradient(circle at ${x}% ${y}%, rgba(${b.color}, 0.75) 0, rgba(${b.color}, 0) 42%)`),
                `radial-gradient(circle, rgba(${b.color}, 0.55) 0, rgba(${b.color}, 0.25) 38%, rgba(${b.color}, 0) 68%)`,
              ].join(", "),
            }}
          />
          {b.dots.map((d, k) => (
            <span key={k} className="ut-blast__dot" style={{ "--dx": `${d.dx}px`, "--dy": `${d.dy}px`, width: d.size, height: d.size }} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default ColorBlast;
